<template>
	<aside class="download-single-select__container">
		<div class="box">
			<div class="col lg-offset-4 lg-8">
				<div class="download-single-select__title-lockup">
					<h2 v-if="headline">{{ headline }}</h2>
					<a
						v-if="items.length > 0 && orderLitURL && buttonText"
						:href="orderLitURL"
						class="download-single-select__title-link"
					>
						{{ buttonText }}
					</a>
				</div>
			</div>

			<ul class="container col lg-offset-4 lg-8">
				<li
					v-for="(item, i) in items"
					:key="'download-' + i"
					class="download-single-select"
				>
					<DownloadSingleSelectItem
						:id="`${singleSelectId}-item-${i}`"
						:displayTitle="item.displayTitle"
						:titleLink="item.titleLink"
						:description="item.description"
						:link="item.relativePath"
						:extraDownload="item.extraDownload"
						:img="item.img"
						:litId="item.literatureId"
						:fundId="item.fundId"
					/>
				</li>
			</ul>
		</div>
	</aside>
</template>

<script>
import DownloadSingleSelectItem from "./base/DownloadSingleSelectItem.vue";
import { UniqueId } from "../../../utilities";

/**
 * A single-select downnload component.
 *
 * ## Props Data
 *
 * ### `items`
 *
 * ```javascript
 * [
 *	{
 *		anchorLink: false, // (Optional) For anchor links, set this item to true
 *		literatureId: "SP40", // (Optional) ID of the literature item.
 *		displayTitle: "Summary prospectus", // (Required) Document title which is displayed on screen
 *		downloadTitle: "VFINX Summary prospectus", // (Required) Title given to downloaded PDF
 *		relativePath: "/pub/Pdf/sp40.pdf", // (Required) Relative path to document (see forceAsAbsolutePath for absolute paths)
 *		forceAsAbsolutePath: false, // (Optional) If the asset download path needs to be absolute, set this to true
 *		titleLink: {
 *			location: "/whitepaper.pdf",
 *			text: "Order Whitepaper"
 *		}, // (Optional) Link next to document title
 *		description:
 *			"A simplified version of a statutory prospectus.", // (Optional) Description of the document
 *		extraDownload: {
 *			location: "/FINRA-letter.pdf",
 *			text: "FINRA Letter"
 *		}, // (Optional) Additional download button next to Download PDF button
 *		img: "/assets/img.jpg" // (Optional) Image of document cover
 *	}
 *]
 * ```
 *
 */
export default {
	name: "DownloadSingleSelect",
	components: { DownloadSingleSelectItem },
	props: {
		/**
		 * The headline text appearing within an h2 or h3 depending on the `headingLevel` prop value.
		 */
		headline: {
			type: String
		},

		/**
		 * Text within the download button.
		 */
		buttonText: {
			type: String
		},

		/**
		 * An array of objects making up the downloadable items. Refer to the documentation above for object properties.
		 */
		items: {
			type: Array,
			required: true
		},

		/**
		 * Add a URL to the button so it behaves like an anchor link rather than using a click event.
		 */
		orderLitURL: {
			type: String
		}
	},
	data() {
		return {
			singleSelectId: new UniqueId("single-select-").generate()
		};
	}
};
</script>

<style lang="scss">
.download-single-select__container {
	background-color: $color--bg-gray;
	margin-bottom: 40px;
	padding: 40px 0;

	@include from-md() {
		padding: 64px 0;
	}
}

.download-single-select {
	border-bottom: 2px solid $color--lighter-gray;
	overflow: hidden;
	padding-bottom: 40px;
	padding-left: 0;
	padding-top: 40px;
	margin: 0;

	&:first-child {
		padding-top: 0;
	}
	&:last-child {
		border-bottom: none;
		margin-bottom: 0;
		padding-bottom: 0;
	}
}

.download-single-select__title-lockup {
	display: flex;
	flex-direction: row;
	flex-wrap: wrap;
	justify-content: space-between;
	margin-bottom: 16px;
}

.download-single-select__title-link {
	@include p3;

	color: $color--black;
	text-decoration: underline;
}
</style>
